import { useEffect, useState } from "react";
import { Sparkles, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import NewsletterForm from "./NewsletterForm";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

// Bandeau promotionnel de l'accueil : texte DÉFILANT de droite à gauche (police
// Cooper Black, la marque) invitant à l'infolettre — le code de bienvenue est
// envoyé par courriel après confirmation (double opt-in). Affiché seulement si un
// code rabais d'accueil est en vigueur (Admin > Paramètres > Codes rabais).
// Une personne CONNECTÉE déjà abonnée à l'infolettre ne voit plus le bandeau.
export function PromoBanner() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [promos, setPromos] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/promo/active").then((res) => setPromos(res.data)).catch(() => {});
  }, []);

  // Abonnée (compte connecté) : bandeau retiré — dès l'inscription, sans recharger
  if (user && user.newsletter) return null;
  // Affiché seulement si une promo d'accueil est en vigueur (jamais pour les codes de parrainage REF10)
  if (!promos.some((x) => x.condition !== "referencement")) return null;

  return (
    <div data-testid="promo-banner"
      className="relative z-30 overflow-hidden text-ink bg-gradient-to-r from-[#c39a2e] via-[#f2d67c] to-[#c39a2e]">
      {/* balayage scintillant discret sur l'or */}
      <div aria-hidden className="pointer-events-none absolute inset-y-0">
        <div className="promo-brille absolute -top-1/2 -bottom-1/2 w-1/4 bg-gradient-to-r from-transparent via-white/60 to-transparent" />
      </div>
      <style>{`
        @keyframes promo-brille {
          0% { transform: translateX(-180%) skewX(-20deg); opacity: 0; }
          15% { opacity: 1; }
          55% { transform: translateX(620%) skewX(-20deg); opacity: 1; }
          62% { opacity: 0; }
          100% { transform: translateX(620%) skewX(-20deg); opacity: 0; }
        }
        .promo-brille { animation: promo-brille 4.2s ease-in-out infinite; }
        /* défilement continu droite -> gauche */
        @keyframes promo-defile { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .promo-cooper { font-family: "Cooper Black","Cooper Std Black",Georgia,serif; }
        .promo-marquee-track { display: flex; width: max-content; animation: promo-defile 26s linear infinite; }
        .promo-marquee:hover .promo-marquee-track { animation-play-state: paused; }
      `}</style>

      {/* Mobile : texte défilant EN HAUT, bouton EN DESSOUS (empilé) ;
          dès sm : texte à gauche, bouton à droite sur la même ligne */}
      <div className="relative flex flex-col sm:flex-row items-center gap-1.5 sm:gap-4 py-2 sm:py-3 px-4 sm:pl-6 sm:pr-4">
        <Sparkles className="w-5 h-5 shrink-0 hidden sm:block" />
        <div className="promo-marquee w-full sm:flex-1 overflow-hidden self-center" data-testid="promo-banner-marquee">
          <div className="promo-marquee-track promo-cooper items-center">
            {[0, 1].map((i) => (
              <span key={i} aria-hidden={i === 1}
                className="whitespace-nowrap text-base sm:text-lg tracking-wide leading-none self-center drop-shadow-[0_1px_0_rgba(255,255,255,0.25)]">
                {t("promo.banner_welcome")}
                <span className="mx-8 text-ink/50">✦</span>
              </span>
            ))}
          </div>
        </div>
        <button type="button" onClick={() => setOpen(true)} data-testid="promo-banner-cta"
          className="shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap bg-ink text-gold text-xs font-bold px-4 sm:px-5 py-2 rounded-full hover:shadow-[0_0_20px_rgba(0,0,0,0.45)] hover:scale-[1.04] transition-all duration-300">
          <Mail className="w-3.5 h-3.5" />
          {t("promo.banner_cta")}
        </button>
      </div>

      {/* Abonnement à la lettre d'information — vérification par courriel (double opt-in) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="promo-banner-dialog" className="bg-surface border-gold/30 text-ivory max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-gold">{t("promo.newsletter.dialog_title")}</DialogTitle>
            <DialogDescription className="text-ivory/60 text-sm leading-relaxed">
              {t("promo.popup_welcome_desc")}
            </DialogDescription>
          </DialogHeader>
          <NewsletterForm source="bandeau" testid="banner-newsletter"
            onSubscribed={() => { if (user) setUser({ ...user, newsletter: true }); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
